// =============================================================================
// src/data/analytics-feed-page-query.ts — Feed paginado e sua contagem.
//
// 2026-09-20: a lista de mencoes deixa de ser a amostra curada dos 7 baldes e
// passa a ser uma JANELA ordenada sobre o conjunto do recorte (42.425 itens no
// escopo padrao, 1.697 paginas de 25). Os baldes NAO somem — viram a tela
// Destaques (decisao do usuario), com o documento do dashboard intacto.
//
// Dois documentos SEPARADOS de proposito. Se `_aggregate` falhar a validacao no
// Hasura, so a contagem cai; dentro do documento do feed ele derrubaria a pagina
// junto, e dentro do dashboard derrubaria os 14 roots — a tela inteira viraria
// base demonstrativa por causa de um contador.
// =============================================================================

import { ANALYTICS_GRAPHQL_ROOTS } from './analytics-graphql-roots';
import { ANALYTICS_FEED_FIELDS_FRAGMENT } from './analytics-live-query';
import { analyticsScopeWhere, type AnalyticsScopeWhereOptions } from './analytics-scope-where';
import type { AnalyticsFeedSort } from './analytics-feed-sort';

/** Itens por pagina (decisao do usuario: cabe numa tela sem rolagem longa). */
export const ANALYTICS_FEED_PAGE_SIZE = 25;

/** Filtros de runtime do feed — canal do rail e drill-down por personagem. */
export interface AnalyticsFeedPageFilters {
  readonly rawPlatforms: readonly string[];
  readonly actorSlugs: readonly string[];
  readonly relations: readonly string[];
  readonly relationPairs: readonly string[];
}

export interface AnalyticsFeedPageQueryOptions extends AnalyticsScopeWhereOptions {
  readonly sort: AnalyticsFeedSort;
  /** 1-based, como o operador ve. */
  readonly page: number;
  readonly filters: AnalyticsFeedPageFilters;
  /** O backend ja projeta jev_sentimento? Pedir antes invalida o documento. */
  readonly withSentiment: boolean;
}

/** Desempate final de TODA ordenacao — ver analyticsFeedOrderBy. */
const ANALYTICS_FEED_TIE_BREAK = '{ content_id: desc }';

const ANALYTICS_FEED_SORT_ORDER_BY: Readonly<Record<AnalyticsFeedSort, readonly string[]>> = {
  'data:desc': ['{ published_at: desc_nulls_last }'],
  'data:asc': ['{ published_at: asc_nulls_last }'],
  'engajamento:desc': ['{ engagement_score: desc }', '{ published_at: desc_nulls_last }'],
  'engajamento:asc': ['{ engagement_score: asc }', '{ published_at: desc_nulls_last }'],
  'vistas:desc': ['{ views_count: desc }', '{ published_at: desc_nulls_last }'],
  'vistas:asc': ['{ views_count: asc }', '{ published_at: desc_nulls_last }'],
  'sentimento:desc': ['{ sentiment_rank: desc_nulls_last }', '{ published_at: desc_nulls_last }'],
  'sentimento:asc': ['{ sentiment_rank: asc_nulls_last }', '{ published_at: desc_nulls_last }'],
};

/**
 * `order_by` da ordenacao ativa, SEMPRE terminando em content_id.
 *
 * O desempate nao e otimizacao, e correcao: 62% do feed tem engagement_score = 0
 * e o Postgres nao garante ordem entre empates. Sem ele, OFFSET 1000 e OFFSET
 * 1025 podem devolver a mesma linha e pular outra — sem erro, sem sintoma.
 * `*_nulls_last` porque o `desc` do Hasura gera DESC NULLS FIRST, e nulo no topo
 * da pagina 1 e exatamente o que ninguem quer ver.
 *
 * @example analyticsFeedOrderBy('data:desc') // '[{ published_at: desc_nulls_last }, { content_id: desc }]'
 */
export function analyticsFeedOrderBy(sort: AnalyticsFeedSort): string {
  return `[${[...ANALYTICS_FEED_SORT_ORDER_BY[sort], ANALYTICS_FEED_TIE_BREAK].join(', ')}]`;
}

/**
 * Offset do servidor para a pagina da UI. Pagina invalida nunca vira negativa.
 *
 * @example analyticsFeedPageOffset(3) // 50
 */
export function analyticsFeedPageOffset(page: number): number {
  return Math.max(0, page - 1) * ANALYTICS_FEED_PAGE_SIZE;
}

function analyticsFeedArrayClause(field: string, values: readonly string[]): string {
  if (values.length === 0) return '';
  return `, ${field}: { _contains: ${JSON.stringify(values)} }`;
}

/** Where compartilhado pela pagina e pela contagem — os dois respondem o MESMO recorte. */
function analyticsFeedPageWhere(options: AnalyticsFeedPageQueryOptions): string {
  const platforms = options.filters.rawPlatforms;
  const platformClause = platforms.length > 0 ? `, platform: { _in: ${JSON.stringify(platforms)} }` : '';
  return `client_id: { _eq: $clientId }, published_at: { _gte: $sinceTs }${platformClause}`
    + analyticsFeedArrayClause('actor_slugs', options.filters.actorSlugs)
    + analyticsFeedArrayClause('actor_relations', options.filters.relations)
    + analyticsFeedArrayClause('actor_relation_pairs', options.filters.relationPairs)
    + `, ${analyticsScopeWhere(options)}`;
}

/** Campos de sentimento fora do fragment compartilhado (que o dashboard tambem usa). */
function analyticsFeedSentimentFields(withSentiment: boolean): string {
  return withSentiment ? ' sentiment_verdict sentiment_rank sentiment_conf' : '';
}

/**
 * Uma pagina do feed. Pede PAGE_SIZE + 1: a 26a linha nunca e renderizada, ela
 * so responde "existe proxima pagina?" sem depender da contagem — que vem por
 * documento proprio e pode falhar sozinha.
 *
 * @example buildAnalyticsFeedPageQuery({ scope: 'nucleo', showReactions: false, sort: 'data:desc', page: 1, filters, withSentiment: false })
 */
export function buildAnalyticsFeedPageQuery(options: AnalyticsFeedPageQueryOptions): string {
  return `
  query AnalyticsFeedPage($clientId: uuid!, $sinceTs: timestamptz!) {
    feedPage: ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeed}(
      where: { ${analyticsFeedPageWhere(options)} }
      order_by: ${analyticsFeedOrderBy(options.sort)}
      limit: ${ANALYTICS_FEED_PAGE_SIZE + 1}
      offset: ${analyticsFeedPageOffset(options.page)}
    ) { ...AnalyticsFeedFields${analyticsFeedSentimentFields(options.withSentiment)} }
  }

  ${ANALYTICS_FEED_FIELDS_FRAGMENT}
`;
}

/**
 * Total do recorte, para "51–75 de 42.425" e para o numero de paginas.
 *
 * Vai contra a view de CHAVES: a permissao anon da v_mentions_feed tem limit
 * 5000 e o Hasura aplica o limite da permissao dentro da subquery da agregacao,
 * entao contar por la responderia 5.000 e a tela mentiria as paginas.
 *
 * @example buildAnalyticsFeedTotalQuery(options)
 */
export function buildAnalyticsFeedTotalQuery(options: AnalyticsFeedPageQueryOptions): string {
  return `
  query AnalyticsFeedTotal($clientId: uuid!, $sinceTs: timestamptz!) {
    feedTotal: ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeedKeysAggregate}(
      where: { ${analyticsFeedPageWhere(options)} }
    ) { aggregate { count } }
  }
`;
}
